import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Church, Map, Calendar, Wallet, MessageSquare } from 'lucide-react';
import { dashboardService } from '../services/api';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardService.getDashboardData();
        setDashboardData(response.data.data);
      } catch (err) {
        setError(`Erro ao carregar dados do dashboard: ${err.response?.data?.message || err.message}`);
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getStatsConfig = (stats) => [
    {
      title: 'Caravanas',
      value: stats?.caravanas || '0',
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      link: '/caravanas'
    },
    {
      title: 'Igrejas',
      value: stats?.igrejas || '0',
      icon: Church,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      link: '/igrejas'
    },
    {
      title: 'Eventos',
      value: stats?.eventos || '0',
      icon: Calendar,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      link: '/eventos'
    },
    {
      title: 'Campos',
      value: stats?.campos || '0',
      icon: Map,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
      link: '/campos'
    },
    {
      title: 'Caixas',
      value: stats?.caixas || '0',
      icon: Wallet,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50',
      link: '/caixas'
    },
    {
      title: 'Ocorrências',
      value: stats?.ocorrencias || '0',
      icon: MessageSquare,
      color: 'text-red-500',
      bgColor: 'bg-red-50',
      link: '/ocorrencias'
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInMinutes = Math.floor((now - past) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `Há ${diffInMinutes} minuto(s)`;
    } else if (diffInMinutes < 1440) { // 24 horas
      const hours = Math.floor(diffInMinutes / 60);
      return `Há ${hours} hora(s)`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `Há ${days} dia(s)`;
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 className="text-2xl font-bold mb-6">Dashboard</h1>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      </div>
    );
  }

  const stats = getStatsConfig(dashboardData?.stats);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link 
              key={index} 
              to={stat.link}
              className="card card-hover block no-underline text-inherit transition-all duration-200 hover:shadow-lg hover:scale-105"
              title={`Ver todos os registros de ${stat.title.toLowerCase()}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${stat.bgColor} ${stat.color} transition-all duration-200 card-icon`}>
                    <Icon size={24} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">{stat.title}</p>
                    <p className="text-2xl font-semibold">{stat.value}</p>
                  </div>
                </div>
                <div className="text-gray-400 hover:text-primary transition-colors duration-200">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Eventos Próximos</h2>
            <Link 
              to="/eventos" 
              className="text-sm text-primary hover:text-primary-hover transition-colors duration-200 flex items-center gap-1"
              title="Ver todos os eventos"
            >
              Ver todos
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </Link>
          </div>
          <div className="space-y-3">
            {dashboardData?.upcomingEvents?.length > 0 ? (
              dashboardData.upcomingEvents.map((evento, index) => (
                <div key={evento.id} className={`border-l-4 pl-4 py-2 rounded-r-md hover:bg-gray-50 transition-colors duration-200 ${
                  index % 3 === 0 ? 'border-blue-500' : 
                  index % 3 === 1 ? 'border-green-500' : 'border-purple-500'
                }`}>
                  <h3 className="font-medium">{evento.nome}</h3>
                  <p className="text-sm text-gray-500">
                    {formatDate(evento.dataInicio)} 
                    {evento.dataFim && ` - ${formatDate(evento.dataFim)}`}
                  </p>
                  <p className="text-sm">Local: {evento.campo || 'N/A'}</p>
                  {evento.descricao && (
                    <p className="text-xs text-gray-400 mt-1">{evento.descricao}</p>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="mx-auto mb-2" size={48} />
                <p>Nenhum evento próximo encontrado</p>
                <Link 
                  to="/eventos" 
                  className="inline-block mt-3 text-sm text-primary hover:text-primary-hover transition-colors duration-200"
                >
                  Criar primeiro evento
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Atividade Recente</h2>
            <span className="text-sm text-gray-400">Últimas ações</span>
          </div>
          <div className="space-y-3">
            {dashboardData?.recentActivities?.length > 0 ? (
              dashboardData.recentActivities.map((atividade, index) => (
                <div key={atividade.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    atividade.tipo === 'CREATE' ? 'bg-green-500' :
                    atividade.tipo === 'UPDATE' ? 'bg-blue-500' :
                    atividade.tipo === 'DELETE' ? 'bg-red-500' :
                    'bg-yellow-500'
                  }`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{atividade.descricao}</p>
                    <p className="text-xs text-gray-500">
                      {atividade.usuario} • {getTimeAgo(atividade.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="mx-auto mb-2" size={48} />
                <p>Nenhuma atividade recente encontrada</p>
                <p className="text-xs mt-2">As ações do sistema aparecerão aqui</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;